import { useEffect, useLayoutEffect, useRef, useState } from "react";
import clsx from "clsx";
import { visibleBoundsOf } from "./utils";
import type { DropdownMenuProps } from "./types";

export const DropdownMenu = ({
  items,
  trigger,
  className,
  menuClassName,
  align = "left",
  disabled = false,
}: DropdownMenuProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Flip above the trigger when the menu would not fit below, e.g. on the last row of a scroller
  useLayoutEffect(() => {
    if (!isOpen) return;

    const container = containerRef.current;
    const menuHeight = menuRef.current?.offsetHeight;
    if (!container || !menuHeight) return;

    const triggerRect = container.getBoundingClientRect();
    const bounds = visibleBoundsOf(container);
    const spaceBelow = bounds.bottom - triggerRect.bottom;
    const fitsAbove = triggerRect.top - bounds.top > menuHeight;
    setPlacement(spaceBelow < menuHeight && fitsAbove ? "top" : "bottom");
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative inline-block text-left",
        disabled && "cursor-not-allowed",
        className,
      )}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        // A disabled button swallows hover, so the events reach a wrapper that may carry a tooltip
        className="disabled:pointer-events-none disabled:opacity-50"
      >
        {trigger}
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className={clsx(
            "absolute min-w-[14rem] bg-white rounded-md shadow-lg border border-gray-200 z-50",
            "py-1",
            align === "left" ? "left-0" : "right-0",
            placement === "top" ? "bottom-full mb-2" : "mt-2",
            menuClassName,
          )}
        >
          {items.map((item, index) => (
            <button
              key={index}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  setIsOpen(false);
                }
              }}
              className={clsx(
                "w-full text-left px-4 py-2 text-sm transition",
                "hover:bg-gray-50",
                item.disabled && "opacity-50 cursor-not-allowed",
              )}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
